import { atom, WritableAtom } from 'jotai';
import { RESET } from 'jotai/utils';

// Type for the update argument, which can be a value, a function, or RESET
type SetStateActionWithReset<Value> = Value | ((prev: Value) => Value) | typeof RESET;

/**
 * Creates a Jotai atom with manual persistence to localStorage.
 *
 * @param key The key for localStorage.
 * @param initialValue The initial value if nothing is in storage.
 * @returns A WritableAtom similar to atomWithStorage but using standard atom features.
 */
export function atomWithManualStorage<Value>(
    key: string,
    initialValue: Value
): WritableAtom<Value, [SetStateActionWithReset<Value>], void> {

    // Function to safely get value from localStorage
    const getStoredValue = (): Value => {
        try {
            const item = localStorage.getItem(key);
            if (item !== null) {
                return JSON.parse(item) as Value;
            }
        } catch (e) {
            console.error(`Error reading localStorage key “${key}”:`, e);
        }
        return initialValue;
    };

    // Function to safely set value to localStorage
    const setStoredValue = (newValue: Value) => {
        try {
            if (newValue === undefined || newValue === null) {
                 localStorage.removeItem(key);
            } else {
                 localStorage.setItem(key, JSON.stringify(newValue));
            }
        } catch (e) {
            console.error(`Error writing localStorage key “${key}”:`, e);
        }
    };

    // Create the base atom with the initial value read from storage
    const baseAtom = atom<Value>(getStoredValue());

    // Create a writable derived atom that handles updates and persistence
    const derivedAtom = atom<Value,
        [SetStateActionWithReset<Value>],
        void
    >(
        (get) => get(baseAtom), // Read function just gets the base atom value
        (get, set, update) => { // Write function handles updates
            let nextValue: Value;

            if (update === RESET) {
                nextValue = initialValue; // Reset to initial value
            } else if (typeof update === 'function') {
                nextValue = (update as (prev: Value) => Value)(get(baseAtom)); // Apply updater function
            } else {
                nextValue = update; // Use the direct value
            }

            // Update the base atom state
            set(baseAtom, nextValue);
            // Persist the new value to localStorage
            setStoredValue(nextValue);
        }
    );

    // Optional: Add onMount logic to potentially re-sync if needed,
    // though the initial read in baseAtom should handle most cases.
    // derivedAtom.onMount = (setAtom) => {
    //     const storedValue = getStoredValue();
    //     setAtom(storedValue);
    // };

    return derivedAtom;
} 